import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, X, Dumbbell, BookOpen } from "lucide-react";
import { toast } from "sonner";

const asList = (d, keys) => {
  if (Array.isArray(d)) return d;
  for (const k of keys) if (Array.isArray(d?.[k])) return d[k];
  return d?.items || d?.results || [];
};

export default function PendingContent() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setData(await api.pendingContent()); }
    catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const review = async (type, id, approved) => {
    setBusy(`${type}-${id}`);
    try {
      const body = { approved, status: approved ? "approved" : "rejected" };
      if (type === "exercise") await api.reviewExercise(id, body);
      else await api.reviewArticle(id, body);
      toast.success(approved ? "Contenuto approvato" : "Contenuto rifiutato");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const exercises = asList(data, ["exercises", "pending_exercises"]);
  const articles = asList(data, ["articles", "pending_articles"]);

  const ReviewCard = ({ item, type, i }) => {
    const id = item.id || item._id;
    const Icon = type === "exercise" ? Dumbbell : BookOpen;
    return (
      <Card className="border-border/70" data-testid={`pending-${type}-${i}`}>
        <CardContent className="flex flex-wrap items-center gap-4 p-4">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold">{item.name || item.title}</p>
            <p className="line-clamp-1 text-xs text-muted-foreground">{item.description || item.summary || item.content}</p>
          </div>
          {item.author_name && <Badge variant="secondary">{item.author_name}</Badge>}
          <div className="flex gap-2">
            <Button size="sm" onClick={() => review(type, id, true)} disabled={busy === `${type}-${id}`} data-testid={`approve-${type}-${i}`}><Check className="mr-1 h-4 w-4" /> Approva</Button>
            <Button size="sm" variant="outline" className="text-destructive" onClick={() => review(type, id, false)} disabled={busy === `${type}-${id}`} data-testid={`reject-${type}-${i}`}><X className="mr-1 h-4 w-4" /> Rifiuta</Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div>
      <PageHeader title="Approvazione contenuti" subtitle="Revisiona esercizi e articoli in attesa." />
      <Tabs defaultValue="exercises">
        <TabsList data-testid="pending-tabs">
          <TabsTrigger value="exercises">Esercizi ({exercises.length})</TabsTrigger>
          <TabsTrigger value="articles">Articoli ({articles.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="exercises" className="mt-4 space-y-3">
          {exercises.length === 0 ? <EmptyState title="Nessun esercizio in attesa" /> : exercises.map((it, i) => <ReviewCard key={it.id || i} item={it} type="exercise" i={i} />)}
        </TabsContent>
        <TabsContent value="articles" className="mt-4 space-y-3">
          {articles.length === 0 ? <EmptyState title="Nessun articolo in attesa" /> : articles.map((it, i) => <ReviewCard key={it.id || i} item={it} type="article" i={i} />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
